/**
 * lib/attachments.ts
 * ------------------------------------------------------------------
 * THE EVIDENCE BEHIND A FINANCE RECORD.
 *
 * A voucher says the school paid P4,500 to Gaborone Stationers. The
 * supplier's invoice, the delivery note and the till slip are what say
 * it was true. Without them a payment is an assertion, and "produce the
 * supporting documentation" is the first thing an auditor asks for and
 * the thing a school most often cannot do.
 *
 * WHAT MAY BE ATTACHED IS A CLOSED LIST. A `kind` names the record type,
 * and the storage path is derived from it — so a rule can be written per
 * kind, and a caller cannot invent a path that no rule covers.
 *
 * THE RECORD IS APPEND-ONLY; THE FILE IS NOT QUITE. Two different needs
 * pull against each other here and both are real:
 *
 *   An attachment on an approved voucher is the support for a payment.
 *   Removing it silently leaves a payment with no evidence and nothing
 *   saying there ever was any. So the RECORD is never deleted.
 *
 *   A file attached to the wrong record — a pupil's birth certificate on
 *   a supplier voucher — is a privacy problem, not litter, and "we
 *   cannot remove it" is not an acceptable answer. So the BYTES can be
 *   removed by a manager, and the record stays, marked superseded with a
 *   reason and by whom.
 *
 * The trail therefore always says a file was attached and what happened
 * to it, which is the property that matters; what it does not do is let
 * an attachment disappear as though it had never existed.
 *
 * PURE. No Firestore, no Storage, no React.
 * ------------------------------------------------------------------ */

export const ATTACHMENT_COLLECTION = 'financeAttachments';

/**
 * What can carry evidence. A closed list on purpose: the storage rules
 * are written per kind, so an open string would let a caller write to a
 * path no rule covers — which in Firebase means the write is refused,
 * silently, at the least convenient moment.
 */
export type AttachmentKind =
  | 'voucher'
  | 'purchaseOrder'
  | 'goodsReceipt'
  | 'requisition'
  | 'payment'
  | 'pettySlip'
  | 'asset'
  | 'journal';

export const ATTACHMENT_KIND_LABEL: Record<AttachmentKind, string> = {
  voucher: 'Payment voucher',
  purchaseOrder: 'Purchase order',
  goodsReceipt: 'Goods received note',
  requisition: 'Requisition',
  payment: 'Receipt',
  pettySlip: 'Petty cash slip',
  asset: 'Asset',
  journal: 'Journal',
};

/** What the evidence IS, so a reader knows what they are looking at. */
export type EvidenceType =
  | 'supplier_invoice'
  | 'delivery_note'
  | 'quote'
  | 'receipt'
  | 'bank_proof'
  | 'contract'
  | 'other';

export const EVIDENCE_LABEL: Record<EvidenceType, string> = {
  supplier_invoice: "Supplier's invoice",
  delivery_note: 'Delivery note',
  quote: 'Quotation',
  receipt: 'Receipt / till slip',
  bank_proof: 'Proof of payment',
  contract: 'Contract or agreement',
  other: 'Other',
};

export interface FinanceAttachment {
  id: string;
  kind: AttachmentKind;
  /** The record it belongs to. */
  recordId: string;
  /** Its own number, so the trail reads without a lookup. */
  recordRef?: string;
  evidence: EvidenceType;
  fileName: string;
  /** Where the bytes are. Absent once superseded. */
  storagePath?: string;
  downloadUrl?: string;
  contentType: string;
  sizeBytes: number;
  note?: string;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: string;
  /** Set when the bytes were removed. The record itself never goes. */
  supersededAt?: string;
  supersededBy?: string;
  supersededByName?: string;
  supersededReason?: string;
}

/* ============================== the rules ============================== */

/** What a school actually attaches: a PDF, or a photograph of paper. */
export const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
];

/**
 * Ten megabytes.
 *
 * A phone photograph of an A4 invoice is two to four; a scanned
 * multi-page contract can reach eight. Below this and people photograph
 * documents in two halves to get them through, which is worse evidence
 * than one large file.
 */
export const MAX_BYTES = 10 * 1024 * 1024;

export function attachmentProblem(
  file: { name: string; type: string; size: number },
): string | null {
  if (!file.name?.trim()) return 'That file has no name.';
  if (!ALLOWED_TYPES.includes(file.type)) {
    return 'Attach a PDF or a photograph. Other file types are refused by the server too, '
      + 'so a spreadsheet or a Word document has to be printed to PDF first.';
  }
  if (!(file.size > 0)) return 'That file is empty.';
  if (file.size > MAX_BYTES) {
    return `That file is ${(file.size / 1024 / 1024).toFixed(1)}MB. The limit is `
      + `${MAX_BYTES / 1024 / 1024}MB — photograph the document in one piece at a lower `
      + 'resolution rather than splitting it in two.';
  }
  return null;
}

/**
 * Where the bytes live.
 *
 * `financeDocuments/{kind}/{recordId}/{stamped file name}`. The kind is a
 * path segment so a storage rule can be written for it, and the name
 * carries a timestamp so re-attaching a file called "invoice.pdf" does
 * not overwrite the first one — which would destroy evidence by doing
 * exactly what the uploader expected.
 */
export function storagePathFor(
  kind: AttachmentKind,
  recordId: string,
  fileName: string,
  at: Date = new Date(),
): string {
  const stamp = at.toISOString().replace(/[^0-9]/g, '').slice(0, 14);
  return `financeDocuments/${kind}/${recordId}/${stamp}-${safeName(fileName)}`;
}

/**
 * A file name Storage and a download header will both accept.
 *
 * Sanitised at STORAGE, never at display: the record keeps the name the
 * person recognises, and only the path is flattened.
 */
export function safeName(name: string): string {
  return (name ?? 'file')
    .normalize('NFD')
    /* Drop the combining marks NFD just separated out, rather than letting
       the substitution below turn each one into a hyphen — otherwise
       "Ramotswe.pdf" comes back as "Ramotswe-.pdf", which is what a
       Setswana surname does to a naive sanitiser. */
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80) || 'file';
}

export const isLive = (a: FinanceAttachment): boolean => !a.supersededAt;

/** What is actually attached to a record right now. */
export function liveAttachments(
  all: FinanceAttachment[],
  kind: AttachmentKind,
  recordId: string,
): FinanceAttachment[] {
  return all
    .filter((a) => a.kind === kind && a.recordId === recordId)
    .filter(isLive)
    .sort((a, b) => (a.uploadedAt ?? '').localeCompare(b.uploadedAt ?? ''));
}

export interface EvidenceGap {
  kind: AttachmentKind;
  recordId: string;
  recordRef: string;
  /** What is missing, in words. */
  missing: string;
}

/**
 * Records that ought to carry evidence and do not.
 *
 * A PAID voucher with nothing attached is the one that matters: the money
 * has gone and there is nothing on file saying what for. An unpaid one is
 * merely incomplete, and flagging it would bury the paid ones.
 */
export function evidenceGaps(
  vouchers: { id: string; voucherNo: string; currentStatus: string }[],
  attachments: FinanceAttachment[],
): EvidenceGap[] {
  const has = new Set(
    attachments.filter(isLive).filter((a) => a.kind === 'voucher').map((a) => a.recordId),
  );
  return vouchers
    .filter((v) => v.currentStatus === 'paid')
    .filter((v) => !has.has(v.id))
    .map((v) => ({
      kind: 'voucher' as const,
      recordId: v.id,
      recordRef: v.voucherNo,
      missing: 'Paid, with nothing on file saying what for.',
    }));
}
